import { Facebook, Linkedin, Twitter, Send, MessageCircle, Link2, Mail, Instagram } from "lucide-react";
import { useState } from "react";

interface ShareButtonsProps {
  title: string;
  slug: string;
  /**
   * - "sidebar": card vertical para colocar dentro do aside (lateral).
   * - "mobile":  card horizontal exibido apenas em telas <lg (fallback no fluxo do artigo).
   * - "all" (padrão antigo): renderiza ambos.
   */
  placement?: "sidebar" | "mobile" | "all";
}

export function ShareButtons({ title, slug, placement = "all" }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const [igHint, setIgHint] = useState(false);
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/blog/${slug}`
      : `https://concursoastor.com.br/blog/${slug}`;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const links = [
    {
      name: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
      Icon: MessageCircle,
      color: "hover:bg-[#25D366] hover:text-white hover:border-[#25D366]",
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      Icon: Facebook,
      color: "hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]",
    },
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      Icon: Linkedin,
      color: "hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]",
    },
    {
      name: "X (Twitter)",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      Icon: Twitter,
      color: "hover:bg-black hover:text-white hover:border-black",
    },
    {
      name: "Telegram",
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
      Icon: Send,
      color: "hover:bg-[#26A5E4] hover:text-white hover:border-[#26A5E4]",
    },
    {
      name: "E-mail",
      href: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
      Icon: Mail,
      color: "hover:bg-primary hover:text-white hover:border-primary",
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const shareToInstagram = async () => {
    try {
      await navigator.clipboard.writeText(`${title} ${url}`);
    } catch {}
    setIgHint(true);
    setTimeout(() => setIgHint(false), 3500);
    const isMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile) {
      // Tenta abrir a câmera de Stories do Instagram via deep link
      window.location.href = "instagram://story-camera";
      setTimeout(() => {
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      }, 800);
    } else {
      window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    }
  };

  const renderButtons = (orientation: "vertical" | "horizontal") => (
    <div className={orientation === "vertical" ? "flex flex-col gap-2" : "flex flex-wrap gap-2"}>
      {links.map(({ name, href, Icon, color }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Compartilhar no ${name}`}
          title={`Compartilhar no ${name}`}
          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground transition-all duration-200 hover:scale-110 ${color}`}
        >
          <Icon size={18} />
        </a>
      ))}
      <button
        type="button"
        onClick={shareToInstagram}
        aria-label="Compartilhar no Instagram Stories"
        title="Compartilhar no Instagram Stories"
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground transition-all duration-200 hover:scale-110 hover:border-[#E1306C] hover:text-white hover:bg-gradient-to-tr hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF]"
      >
        <Instagram size={18} />
      </button>
      <button
        type="button"
        onClick={copyLink}
        aria-label="Copiar link"
        title={copied ? "Link copiado!" : "Copiar link"}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground transition-all duration-200 hover:scale-110 hover:border-primary hover:bg-primary hover:text-white"
      >
        <Link2 size={18} />
      </button>
    </div>
  );

  const sidebar = (
    <div className="card-glass">
      <p className="text-sm font-semibold">Compartilhar</p>
      <p className="mt-1 text-xs text-muted-foreground">Envie este artigo para quem precisa.</p>
      <div className="mt-3 flex flex-wrap gap-2">{renderButtons("horizontal")}</div>
      {(copied || igHint) && (
        <p className="mt-3 text-xs text-primary">
          {copied ? "Link copiado!" : "Link copiado — cole no seu Story do Instagram!"}
        </p>
      )}
    </div>
  );

  const mobile = (
    <div className="my-8 rounded-2xl border border-border bg-secondary/40 p-5 lg:hidden">
      <p className="text-sm font-semibold">Compartilhe este artigo</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Ajude mais pessoas a encontrarem este conteúdo.
      </p>
      <div className="mt-4">{renderButtons("horizontal")}</div>
      {(copied || igHint) && (
        <p className="mt-3 text-xs text-primary">
          {copied ? "Link copiado!" : "Link copiado — abra o Instagram e cole no seu Story!"}
        </p>
      )}
    </div>
  );

  if (placement === "sidebar") return sidebar;
  if (placement === "mobile") return mobile;
  return (<>{sidebar}{mobile}</>);
}